// useLocalStorage.js
import { useState } from "react";

/**
 * A custom hook that syncs state with localStorage.
 *
 * @param {string} key - The key under which to store the value in localStorage.
 * @param {*} initialValue - The initial value used if localStorage does not have data.
 * @returns {[any, function]} - Returns the stored value and a setter function.
 */
export function useLocalStorage(key, initialValue) {
  // Get initial state from localStorage if present; otherwise, use the provided initial value.
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item !== null ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter that saves to localStorage.
  const setValue = (value) => {
    try {
      // If the value is a function, then get the new value by calling it with the previous value.
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
}
